// Admin access is per-area. The backend resolves wildcards and overrides for
// the current user (GET /v1/users/me/permissions); the grant editor needs the
// same rules client-side to preview what a set of grant strings will mean.

// Same order as the API returns them.
export const AREAS = [
  "admin/users",
  "admin/counters",
  "admin/credits",
  "admin/cards",
  "admin/badges",
  "admin/clubs",
  "admin/events",
  "admin/configs",
  "admin/safety",
  "admin/features",
  "admin/waitlist",
  "admin/special-users",
  "admin/costs",
  "admin/announcements",
  "admin/storage",
  "admin/sharding",
  "docs",
] as const;

export type Area = (typeof AREAS)[number];
export type Level = "none" | "read" | "write";

export const WILDCARD = "admin/*";
export type GrantTarget = Area | typeof WILDCARD;
export type GrantString = `${GrantTarget}:${Level}`;

export const LEVELS: readonly Level[] = ["none", "read", "write"];

export const AREA_LABELS: Record<Area, string> = {
  "admin/users": "Users",
  "admin/counters": "Counters",
  "admin/credits": "Credits",
  "admin/cards": "Profile cards",
  "admin/badges": "Badges",
  "admin/clubs": "Clubs",
  "admin/events": "Events",
  "admin/configs": "Configs",
  "admin/safety": "Safety",
  "admin/features": "Feature flags",
  "admin/waitlist": "Waitlist",
  "admin/special-users": "Special users",
  "admin/costs": "Costs",
  "admin/announcements": "Announcements",
  "admin/storage": "Storage",
  "admin/sharding": "Sharding",
  docs: "Swagger docs",
};

export const AREA_DESCRIPTIONS: Record<Area, string> = {
  "admin/users": "User stats, block lists, notifications, profile pictures",
  "admin/counters": "Global and per-user counters",
  "admin/credits": "Credit balances: read shows a user's credit ledger, write adjusts balances",
  "admin/cards": "Profile cards: create, edit, images, grant/revoke",
  "admin/badges": "Badges, images, variants, grant/revoke",
  "admin/clubs": "Club moderation: members, invitations, force-delete",
  "admin/events": "Events, boosts, event badges, tiers",
  "admin/configs": "Config ledger: view, publish, rollback, promote",
  "admin/safety": "Abuse reports and enforcements",
  "admin/features": "Feature-flag management",
  "admin/waitlist": "Waitlist signups and CSV export — read-only, no writes exist",
  "admin/special-users": "This screen — granting and revoking access",
  "admin/costs": "Infra cost data — LLM token usage. Read-only, no writes exist",
  "admin/announcements": "In-app announcement slides: create, edit, enable/disable, delete",
  "admin/storage": "Admin-uploaded file browser: list, upload, delete images",
  "admin/sharding":
    "Configured server nodes and their locations. Read-only, no writes exist",
  docs: "Swagger docs in production. Not part of the admin UI.",
};

// Areas with no write routes at all: "read" is already full access there, so
// telling the user that writing is disabled would be noise.
export const READ_ONLY_AREAS: readonly Area[] = [
  "admin/waitlist",
  "admin/costs",
  "admin/sharding",
];

// ── The current user's resolved permissions ───────────────────────────────────

export interface MyPermissions {
  permissions: Record<Area, Level>;
  ratelimit_bypass: boolean;
}

export const NO_PERMISSIONS: MyPermissions = {
  permissions: Object.fromEntries(AREAS.map((a) => [a, "none"])) as Record<
    Area,
    Level
  >,
  ratelimit_bypass: false,
};

// An area the server doesn't know about yet (a UI deployed ahead of its
// backend) must read as "none" — an undefined level would otherwise pass every
// `!== "none"` check and unlock the screen.
export function normalizeMyPermissions(raw: MyPermissions): MyPermissions {
  return {
    ratelimit_bypass: raw?.ratelimit_bypass ?? false,
    permissions: Object.fromEntries(
      AREAS.map((area) => [area, raw?.permissions?.[area] ?? "none"]),
    ) as Record<Area, Level>,
  };
}

export const canRead = (me: MyPermissions, a: Area) =>
  me.permissions[a] !== "none";

export const canWrite = (me: MyPermissions, a: Area) =>
  me.permissions[a] === "write";

export const hasAnyAdmin = (me: MyPermissions) =>
  AREAS.some((a) => a !== "docs" && me.permissions[a] !== "none");

// ── Grant strings ─────────────────────────────────────────────────────────────

export function isArea(value: string): value is Area {
  return (AREAS as readonly string[]).includes(value);
}

function isLevel(value: string): value is Level {
  return (LEVELS as readonly string[]).includes(value);
}

export interface ParsedGrant {
  target: GrantTarget;
  level: Level;
}

export function parseGrant(entry: string): ParsedGrant | null {
  const at = entry.lastIndexOf(":");
  if (at < 0) return null;
  const target = entry.slice(0, at);
  const level = entry.slice(at + 1);
  if (!isLevel(level)) return null;
  if (target === WILDCARD) return { target: WILDCARD, level };
  if (isArea(target)) return { target, level };
  return null;
}

export const formatGrant = (target: GrantTarget, level: Level): GrantString =>
  `${target}:${level}` as GrantString;

// The stricter of two levels wins when an area is listed twice.
export function strictest(a: Level, b: Level): Level {
  return LEVELS.indexOf(a) < LEVELS.indexOf(b) ? a : b;
}

export interface ResolvedGrants {
  /** What each area actually resolves to, wildcard and overrides applied. */
  effective: Record<Area, Level>;
  /** Explicit per-area entries, before the wildcard is considered. */
  explicit: Partial<Record<Area, Level>>;
  /** The wildcard level, or null when no wildcard entry is present. */
  wildcard: Level | null;
  /** Entries the catalog doesn't recognise — only possible via hand edits. */
  unknown: string[];
}

export function resolveGrants(entries: readonly string[]): ResolvedGrants {
  const explicit: Partial<Record<Area, Level>> = {};
  const unknown: string[] = [];
  let wildcard: Level | null = null;

  for (const entry of entries) {
    const parsed = parseGrant(entry);
    if (!parsed) {
      unknown.push(entry);
      continue;
    }
    if (parsed.target === WILDCARD) {
      wildcard = wildcard === null ? parsed.level : strictest(wildcard, parsed.level);
      continue;
    }
    const current = explicit[parsed.target];
    explicit[parsed.target] =
      current === undefined ? parsed.level : strictest(current, parsed.level);
  }

  const effective = Object.fromEntries(
    AREAS.map((area) => {
      const own = explicit[area];
      if (own !== undefined) return [area, own];
      // The wildcard covers every admin/… area but never docs.
      if (area !== "docs" && wildcard !== null) return [area, wildcard];
      return [area, "none" as Level];
    }),
  ) as Record<Area, Level>;

  return { effective, explicit, wildcard, unknown };
}

// ── Editor state ──────────────────────────────────────────────────────────────

/** "unset" writes no entry at all, so the wildcard decides the area. */
export type AreaSetting = Level | "unset";
/** "off" writes no wildcard entry. */
export type WildcardSetting = Level | "off";

export interface GrantDraft {
  wildcard: WildcardSetting;
  areas: Record<Area, AreaSetting>;
}

export function draftFromGrants(entries: readonly string[]): GrantDraft {
  const { explicit, wildcard } = resolveGrants(entries);
  return {
    wildcard: wildcard ?? "off",
    areas: Object.fromEntries(
      AREAS.map((area) => [area, explicit[area] ?? "unset"]),
    ) as Record<Area, AreaSetting>,
  };
}

export function draftToGrants(draft: GrantDraft): GrantString[] {
  const out: GrantString[] = [];
  if (draft.wildcard !== "off") out.push(formatGrant(WILDCARD, draft.wildcard));
  for (const area of AREAS) {
    const setting = draft.areas[area];
    if (setting !== "unset") out.push(formatGrant(area, setting));
  }
  return out;
}

export function draftEffective(draft: GrantDraft): Record<Area, Level> {
  return resolveGrants(draftToGrants(draft)).effective;
}

/** One-line summary of what a set of effective levels actually unlocks. */
export function describeEffective(effective: Record<Area, Level>): string {
  const write = AREAS.filter((a) => effective[a] === "write");
  const read = AREAS.filter((a) => effective[a] === "read");
  if (!write.length && !read.length) return "No access to anything.";

  const label = (areas: Area[]) => areas.map((a) => AREA_LABELS[a]).join(", ");
  const adminAreas = AREAS.filter((a) => a !== "docs");
  const parts: string[] = [];

  if (adminAreas.every((a) => effective[a] === "write")) {
    parts.push(
      effective.docs === "write"
        ? "Full access to every admin area and Swagger docs"
        : "Full access to every admin area",
    );
  } else if (write.length) {
    parts.push(`Full access to ${label(write)}`);
  }

  const remainingRead = adminAreas.every((a) => effective[a] === "write")
    ? read.filter((a) => a === "docs")
    : read;
  if (adminAreas.every((a) => effective[a] === "read")) {
    parts.push("read-only on every admin area");
    if (effective.docs === "read") parts.push("read-only on Swagger docs");
  } else if (remainingRead.length) {
    parts.push(`read-only on ${label(remainingRead)}`);
  }

  return `${parts.join(", ")}.`;
}
